/**
 * /w/:slug/app/monitoring — V113-7-2 / 7-4 (ux-adversary run v113-monitoring, round 4; D-113-29/30/31).
 *
 * KPI tiles (signed pack · controls passing · open findings · unmonitored configuration items), the Next strip
 * (the most urgent system's own next action), the Systems table (owner + class in the name cell, configuration
 * items, passing, open findings, the pack chip + its lines, one row action from `next_action`) and Shared feeds
 * (pack validity, history, reasons, re-sign with carried exceptions, recipients + one-time tokens).
 * Reads: GET /monitoring/systems, /monitoring/pack-validity, /configuration-items (any member); /monitoring/feeds
 * (owner / admin). Delta chips render only when the API sends a delta (today: never).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, KpiStrip, KpiTile, PageHeader, Spinner } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useMonitoringCapabilities, useMonitoringSummary, usePackValidity } from '../hooks/useMonitoring';
import type { MonitoringSummaryRow } from '../lib/monitoring-summary';
import { listMonitoringPlanPackages } from '../lib/monitoring-api';
import {
  attestedLine, CLASS_ANCHOR, carriedLine, carriedText, classLabel, issuesText, monitoringFindingsHref, nextActionHref, pickNext, reasonShort,
  systemTabHref, worstFirst, CHANGES_ANCHOR,
} from '../lib/monitoring-view';
import { fmtDate, plural } from '../lib/format';
import { ActionDialog, useAnnounce } from '../components/today/shared';
import { CauseLine, PackChip, ProgressLines, StatusChip } from '../components/monitoring/parts';
import { SharedFeeds } from '../components/monitoring/SharedFeeds';
import './today.css';
import './program-screens.css';
import './monitoring.css';

export function ClassCell({ row }: { row: Pick<MonitoringSummaryRow, 'system_class' | 'system_class_source' | 'owner_name'> }) {
  return (
    <span className="mon-line">
      {!row.system_class ? <StatusChip label="No class" tone="outline" glyph="circle" />
        : row.system_class_source === 'inferred' ? <StatusChip label={`${classLabel(row.system_class)} · inferred`} tone="blue" glyph="unknown" />
          : <span>{classLabel(row.system_class)}</span>}
      {row.owner_name ? <span>· Owner {row.owner_name}</span> : <span>· No owner</span>}
    </span>
  );
}

export function PackCell({ row }: { row: MonitoringSummaryRow }) {
  const reasons = row.pack_reasons.filter((c) => c !== 'new_system_not_in_pack').map(reasonShort);
  const attested = attestedLine(row.attested_pass, row.attested_of);
  return (
    <span className="mon-cell-stack">
      <PackChip state={row.pack_state} />
      {row.pack_state === 'not_in_pack' ? <span className="mon-sub">{row.system_class ? 'In the next signed pack' : 'Needs a class to join a pack'}</span> : null}
      {row.pack_state === 'current' ? <span className="mon-sub">Unchanged since attested</span> : null}
      {carriedLine(row.carried) ? <span className="mon-sub">{carriedLine(row.carried)}</span> : null}
      {reasons.length ? <span className="mon-sub">{reasons.join(' · ')}</span> : null}
      {row.pack_state && row.pack_state !== 'not_in_pack' ? <span className="mon-sub">{[attested, issuesText(row.open_issues)].filter(Boolean).join(' · ')}</span> : null}
    </span>
  );
}

function csv(rows: MonitoringSummaryRow[]): string {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const head = ['System', 'Class', 'Class source', 'Owner', 'Configuration items', 'Unmonitored', 'Passing', 'Of', 'Have a method', 'Open issues', 'Due', 'Pack', 'Carried', 'New', 'Next action'];
  const lines = rows.map((r) => [r.name, classLabel(r.system_class), r.system_class_source ?? '', r.owner_name ?? '', r.configuration_items_total, r.unmonitored_components,
    r.passing, r.of, r.covered, r.open_issues, r.sla_due?.slice(0, 10) ?? '', r.pack_state ?? 'no pack', carriedText(r.carried), r.new_since_pack ? 'yes' : '', r.next_action?.label ?? ''].map(esc).join(','));
  return [head.map(esc).join(','), ...lines].join('\n');
}

function PlansDialog({ open, slug, prefix, rows, onClose }: { open: boolean; slug: string; prefix: string; rows: MonitoringSummaryRow[]; onClose: () => void }) {
  const classed = rows.filter((r) => r.system_class);
  const plans = useQueries({
    queries: classed.map((r) => ({ queryKey: ['monitoring', slug, 'plan-packages', r.system_id], queryFn: () => listMonitoringPlanPackages(slug, r.system_id), enabled: open, retry: false })),
  });
  const byId = new Map(classed.map((r, i) => [r.system_id, plans[i]]));
  return (
    <ActionDialog open={open} title="Monitoring plans" submitLabel="Close" hideSubmit cancelLabel="Close" onClose={onClose} onSubmit={() => undefined}>
      <ul className="mon-rows">
        {rows.map((r) => {
          const q = byId.get(r.system_id);
          const latest = q?.data?.packages[0];
          const fresh = q?.data?.freshness;
          const line = !r.system_class ? 'Needs a class first'
            : q?.isLoading ? 'Loading…'
              : latest ? `Signed ${fmtDate(latest.signed_at)} · v${latest.version}${fresh?.out_of_date ? ` · out of date since ${fmtDate(fresh.since)}` : ''}`
                : r.system_class_source === 'inferred' ? 'Not signed · confirm class first' : 'Not signed';
          return (
            <li key={r.system_id} className="mon-row">
              <span className="mon-row-main"><span className="mon-name">{r.name}</span><span className="mon-sub">{line}</span></span>
              <span className="mon-row-acts">
                {r.system_class
                  ? <Link className="gp-linkbtn gp-linkbtn--compact" to={systemTabHref(prefix, r.system_id, 'mon-plan')} onClick={onClose}>Open<span className="gp-sr-only"> {r.name} plan</span></Link>
                  : <Link className="gp-linkbtn gp-linkbtn--compact" to={systemTabHref(prefix, r.system_id, CLASS_ANCHOR)} onClick={onClose}>Set class<span className="gp-sr-only"> for {r.name}</span></Link>}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mon-hint">Post-market monitoring plans (EU AI Act Art. 72)</p>
    </ActionDialog>
  );
}

export function Monitoring() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const prefix = `/w/${slug}/app`;
  const summary = useMonitoringSummary();
  const validityQ = usePackValidity();
  const caps = useMonitoringCapabilities();
  const [announcer, announce] = useAnnounce();
  const [worst, setWorst] = useState(true);
  const [plansOpen, setPlansOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);

  const data = summary.data;
  // Per system from the summary (configuration_items_total); the workspace figure sums them (an item shared by two systems counts twice, like the unmonitored tile).
  const totalItems = data ? data.systems.reduce((n, r) => n + r.configuration_items_total, 0) : null;

  if (summary.loading) return <div className="mon-page"><Spinner label="Loading monitoring" /></div>;
  if (summary.error || !data) {
    return (
      <div className="mon-page">
        <PageHeader variant="app" title="Monitoring" headingId="mon-h1" />
        <Alert variant="error">Monitoring could not be loaded. <Button compact onClick={() => void summary.refetch()}>Retry</Button></Alert>
      </div>
    );
  }

  const rows = worst ? worstFirst(data.systems) : [...data.systems].sort((a, b) => a.name.localeCompare(b.name));
  const next = pickNext(data.systems);
  const validity = validityQ.data?.validity ?? null;
  const history = validityQ.data?.history ?? [];
  const noSources = data.systems.length > 0 && data.tiles.covered === 0 && !data.pack;
  const nextDue = data.systems.map((r) => r.sla_due).filter((d): d is string => !!d).sort()[0] ?? null;
  const unmonitoredRows = data.systems.filter((r) => r.unmonitored_components > 0);
  // U-9: "See why" opens the reasons (not just scrolls to them) and lands focus on their summary.
  const openReasons = () => {
    const d = document.getElementById('mon-reasons') as HTMLDetailsElement | null;
    if (!d) return;
    d.open = true;
    d.scrollIntoView?.({ block: 'start' });
    d.querySelector('summary')?.focus();
  };
  const t = data.tiles;

  const header = (
    <PageHeader variant="app" title="Monitoring" headingId="mon-h1"
      subtitle={`${plural(data.systems.length, 'AI system')}${totalItems !== null ? ` · ${plural(totalItems, 'configuration item')}` : ''}`}
      right={data.systems.length ? <div className="mon-head-actions"><Button onClick={() => setPlansOpen(true)}>Monitoring plan</Button></div> : undefined} />
  );

  if (!data.systems.length) {
    return (
      <div className="mon-page">
        {header}
        <EmptyState title="No AI systems yet" description="Monitoring starts once a system is registered."
          action={<Link className="gp-linkbtn btn-primary" to={`${prefix}/systems/new`}>Add an AI system</Link>} />
      </div>
    );
  }

  const nextRow = next;
  const nextTone = nextRow?.pack_state === 'invalidated' || nextRow?.next_action?.kind === 'fix_failing' ? 'red' : nextRow?.pack_state === 'stale' ? 'amber' : '';

  return (
    <div className="mon-page">
      {announcer}
      {header}

      {!noSources ? (
        <KpiStrip label="Monitoring at a glance">
          <KpiTile label="Signed pack" value={<PackChip state={data.pack?.status ?? null} large />}
            sub={<>
              {!data.pack ? 'Nothing shared yet' : carriedText(data.pack.carried) || (data.pack.reason_count ? plural(data.pack.reason_count, 'reason') : `v${data.pack.version} · ${fmtDate(data.pack.signed_at)}`)}
              {data.pack && (data.pack.reason_count || data.pack.carried) ? <> · <a href="#mon-reasons" onClick={(e) => { e.preventDefault(); openReasons(); }}>See why</a></> : null}
            </>} />
          <KpiTile label="Controls passing" value={t.passing} of={`of ${t.of}`}
            valueExtra={t.passing_delta_7d !== null ? <StatusChip label={`${t.passing_delta_7d > 0 ? '▲' : '▼'} ${Math.abs(t.passing_delta_7d)} this week`} tone="muted" /> : null}
            sub={`${t.covered} of ${t.of} have a method${data.unclassed_systems ? ` · ${plural(data.unclassed_systems, 'system')} not counted` : ''}`} />
          <KpiTile label="Open findings" value={t.open_issues}
            sub={<>{nextDue ? `Next due ${fmtDate(nextDue)} · ` : ''}<Link to={t.open_issues ? monitoringFindingsHref(prefix) : `${prefix}/findings`}>{t.open_issues ? 'Open findings' : 'Findings'}</Link></>} />
          <KpiTile label="Unmonitored configuration items" value={t.unmonitored_components} of={totalItems !== null ? `of ${totalItems}` : undefined}
            valueExtra={t.unmonitored_delta !== null ? <StatusChip label={`${t.unmonitored_delta > 0 ? '▲' : '▼'} ${Math.abs(t.unmonitored_delta)}`} tone="muted" /> : null}
            sub={!t.unmonitored_components ? 'All monitored'
              : unmonitoredRows.length === 1 ? <>In {unmonitoredRows[0]!.name} · <Link to={systemTabHref(prefix, unmonitoredRows[0]!.system_id, 'mon-items')}>Review</Link></>
                : <>{unmonitoredRows.map((r, i) => <span key={r.system_id}>{i ? ' · ' : ''}<Link to={systemTabHref(prefix, r.system_id, 'mon-items')}>{r.name} {r.unmonitored_components}</Link></span>)}</>} />
        </KpiStrip>
      ) : null}

      {noSources ? (
        <section className="mon-next" aria-labelledby="mon-next-h">
          <div className="mon-next-body">
            <p className="mon-eyebrow">Next</p>
            <h2 className="mon-next-title" id="mon-next-h">Connect your first source</h2>
            <p className="mon-meta">Read-only. Finds your guardrails, gateways and monitors.</p>
          </div>
          <Link className="gp-linkbtn btn-primary" to={`${prefix}/setup`}>Connect a source</Link>
        </section>
      ) : nextRow?.next_action ? (
        <section className={`mon-next${nextTone ? ` mon-next--${nextTone}` : ''}`} aria-labelledby="mon-next-h">
          <div className="mon-next-body">
            <p className="mon-eyebrow">Next</p>
            <h2 className="mon-next-title" id="mon-next-h">{nextRow.name}: {nextRow.next_action.problem || nextRow.next_action.label}</h2>
            <CauseLine cause={nextRow.next_action.cause} seeHref={systemTabHref(prefix, nextRow.system_id, CHANGES_ANCHOR)} />
            <p className="mon-meta">
              {nextRow.pack_state === 'invalidated' ? <span>Invalidates the pack</span> : nextRow.pack_state === 'current_with_exceptions' ? <span>Carried as an exception</span> : nextRow.pack_state === 'stale' ? <span>Makes the pack stale</span> : null}
              {nextRow.sla_due ? <span>Due {fmtDate(nextRow.sla_due)}</span> : null}
              {nextRow.owner_name ? <span>Owner {nextRow.owner_name}</span> : null}
            </p>
          </div>
          <Link className="gp-linkbtn btn-primary" to={nextActionHref(prefix, nextRow.next_action)}>{nextRow.next_action.label}</Link>
        </section>
      ) : null}

      <Card title="Systems" headingId="mon-systems-h" flush density="compact" right={
          <div className="mon-actions">
            <span className="mon-seg" role="group" aria-label="Sort systems">
              <button type="button" aria-pressed={worst} onClick={() => setWorst(true)}>Worst first</button>
              <button type="button" aria-pressed={!worst} onClick={() => setWorst(false)}>A–Z</button>
            </span>
            <Button compact onClick={() => {
              const blob = new Blob([csv(rows)], { type: 'text/csv' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob); a.download = 'monitoring-systems.csv'; a.click();
              setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            }}>Export CSV</Button>
          </div>}>
        <table className="mon-table" role="table" aria-labelledby="mon-systems-h">
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader" scope="col">System</th>
              <th role="columnheader" scope="col">Configuration items</th>
              <th role="columnheader" scope="col">Controls passing</th>
              <th role="columnheader" scope="col">Open findings</th>
              <th role="columnheader" scope="col">Pack</th>
              <th role="columnheader" scope="col"><span className="gp-sr-only">Next action</span></th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {rows.map((r) => (
              <tr role="row" key={r.system_id} data-system={r.system_id}>
                <td role="cell" className="mon-td-name" data-label="System">
                  <span className="mon-cell-stack">
                    <Link className="mon-name mon-row-link" to={systemTabHref(prefix, r.system_id)}>{r.name}</Link>
                    <ClassCell row={r} />
                    <ProgressLines row={r} showDue={false} />
                  </span>
                </td>
                <td role="cell" data-label="Configuration items">
                  <span className="mon-cell-stack">
                    <span className="mon-cell-main">{r.configuration_items_total}</span>
                    {r.unmonitored_components ? <span className="mon-sub">{r.unmonitored_components} unmonitored</span> : null}
                    {r.pending_review_components ? <span className="mon-sub">{r.pending_review_components} in review</span> : null}
                  </span>
                </td>
                <td role="cell" data-label="Controls passing">
                  {r.system_class ? (
                    <span className="mon-cell-stack"><span className="mon-cell-main">{r.passing} of {r.of}</span><span className="mon-sub">{r.covered} of {r.of} have a method</span>
                      {r.excluded_controls ? <span className="mon-sub">{r.excluded_controls} excluded</span> : null}</span>
                  ) : <span className="mon-cell-stack"><span className="mon-cell-main">—</span><span className="mon-sub">needs a class</span></span>}
                </td>
                <td role="cell" data-label="Open findings">
                  <span className="mon-cell-stack"><span className="mon-cell-main">{r.open_issues ? <Link to={monitoringFindingsHref(prefix, { system: r.system_id })}>{r.open_issues}<span className="gp-sr-only"> open findings · {r.name}</span></Link> : r.open_issues}</span>{r.sla_due ? <span className="mon-sub">Due {fmtDate(r.sla_due)}</span> : null}</span>
                </td>
                <td role="cell" data-label="Pack"><PackCell row={r} /></td>
                <td role="cell" className="mon-act" data-label="">
                  {r.next_action ? <Link className="gp-linkbtn gp-linkbtn--compact" to={nextActionHref(prefix, r.next_action)}>{r.next_action.label}<span className="gp-sr-only"> · {r.name}</span></Link> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.truncated ? <p className="mon-hint mon-hint--pad">Showing the first {data.limit} systems by name. <Link to={`${prefix}/systems`}>All systems</Link></p> : null}
      </Card>

      <Card title="Shared feeds" headingId="mon-feeds-h" density="compact" right={data.pack ? <StatusChip label={`v${data.pack.version} signed ${fmtDate(data.pack.signed_at)}`} tone="outline" /> : undefined}>
        {validityQ.loading ? <Spinner label="Loading pack validity" /> : validityQ.error ? (
          <Alert variant="error">Pack validity could not be loaded. <Button compact onClick={() => void validityQ.refetch()}>Retry</Button></Alert>
        ) : (
          <SharedFeeds slug={slug} prefix={prefix} canManage={caps.manage_feeds} validity={validity} history={history} rows={data.systems}
            onAnnounce={announce} signOpen={signOpen} setSignOpen={setSignOpen} />
        )}
      </Card>

      <PlansDialog open={plansOpen} slug={slug} prefix={prefix} rows={rows} onClose={() => setPlansOpen(false)} />
    </div>
  );
}
